import { Group, Burger, Tooltip, Menu, Avatar, Text, UnstyledButton, ActionIcon } from '@mantine/core'
import { IconSun, IconMoon, IconAlertTriangle } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { useTheme, Breadcrumbs } from '@lighthouse/design'
import { useCurrentUser, useSignOut, useProfilePhoto } from '../auth'
import { useBreadcrumbs } from '../nav/useBreadcrumbs'
import { useClock } from '../hooks/useClock'
import { useEnvironment } from '../hooks/useEnvironment'

interface AppHeaderProps {
  navbarOpened: boolean
  setNavbarOpened: (opened: boolean) => void
}

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).map((p) => p[0]).join('').toUpperCase().slice(0, 2)
}

export function AppHeader({ navbarOpened, setNavbarOpened }: AppHeaderProps) {
  const { mode, toggleTheme } = useTheme()
  const user = useCurrentUser()
  const signOut = useSignOut()
  const profilePhoto = useProfilePhoto()
  const navigate = useNavigate()
  const crumbs = useBreadcrumbs()
  const clock = useClock()
  const env = useEnvironment()

  const nextModeLabel = mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'

  return (
    <div className="lh-header">
      <Group gap="sm" style={{ minWidth: 0 }}>
        <Burger
          opened={navbarOpened}
          onClick={() => setNavbarOpened(!navbarOpened)}
          hiddenFrom="sm"
          size="sm"
        />
        {/* The brand block lives on the rail; the header carries orientation. */}
        <Breadcrumbs
          items={crumbs.map((c, i) => ({
            label: c.label,
            onClick: c.path && i < crumbs.length - 1 ? () => navigate(c.path!) : undefined,
          }))}
        />
      </Group>

      <div className="lh-header-right">
        {env.showBadge && (
          <Tooltip label={`You are looking at the ${env.label} environment`} withArrow>
            <span className="lh-env-badge">
              <IconAlertTriangle size={11} stroke={2.5} />
              {env.label}
            </span>
          </Tooltip>
        )}

        <div className="lh-clock">
          <span className="lh-clock-time">
            {clock.time} <span className="lh-clock-zone">{clock.zone}</span>
          </span>
          <span className="lh-clock-date">{clock.date}</span>
        </div>

        <Tooltip label={nextModeLabel} withArrow>
          <ActionIcon variant="subtle" onClick={toggleTheme} aria-label={nextModeLabel}>
            {mode === 'dark' ? <IconSun size={17} stroke={1.9} /> : <IconMoon size={17} stroke={1.9} />}
          </ActionIcon>
        </Tooltip>

        {user && (
          <Menu shadow="md" width={230} position="bottom-end">
            <Menu.Target>
              <UnstyledButton aria-label="User menu">
                <Avatar color="sea" radius="xl" size="sm" src={profilePhoto}>
                  {getInitials(user.name)}
                </Avatar>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item component="div" closeMenuOnClick={false} style={{ cursor: 'default' }}>
                <Text size="sm" fw={500}>{user.name}</Text>
                <Text size="xs" c="dimmed">{user.email}</Text>
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={signOut}>Sign out</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
    </div>
  )
}
